
import { Button, ClickAwayListener, Grid, InputAdornment } from '@mui/material';
import { COLOR_MAP } from '../../../constants/ColorNames';
import styled from '@emotion/styled';
import SingleSelectDropDown from '../../../common/components/SingleSelectDropDown';
import TextBox from '../../../common/components/Textbox';
import React, { useEffect } from 'react';
import DateRangePicker from '../../../common/components/DateRangePicker';
import { useGetCampaignStatusListQuery } from '../../../redux/services/folderName';

import { getDateRange } from '../../../utils/helpers';
import { SEARCH_ICON } from '../../../constants/icons/SearchIcon';
import { FILTER_ICON } from '../../../constants/icons/FilterIcon';
import { FiltersType } from '../../../types/dataTypes';
import { CALENDAR_ICON } from '../../../constants/icons/CalendarIcon';

type FilterProps = {
  pageNo: number,
  handleFitersValueChange: (filters: FiltersType) => void;
  filteredCampaignName?:string 
}

type FiltersValueType = {
  campaignName: string,
  campaignStatus: string,
  dateRange: string,
  // campaignId: string | number
}

const DATE_RANGE_VALUES = [
  { label: 'Last 24 hours', value: 'Last 24 hours' },
  { label: 'Last 3 days', value: 'Last 3 days' },
  { label: 'Last 7 days', value: 'Last 7 days' },
  { label: 'Last 1 month', value: 'Last 1 month' },
  { label: 'Last 3 months', value: 'Last 3 months' },
  { label: 'Last 6 months', value: 'Last 6 months' },
  { label: 'Custom Date', value: 'Custom Date' },
]

const initialFilters = {
  campaignName: '',
  campaignStatus: '',
  dateRange: '',
  // campaignId:''
}
const FilterWrapper = styled(Grid)`
   padding: 1.6rem 4rem 2rem 4rem !important;
  align-items: center !important;
`;

const FilterHeading = styled.p`
  font-weight:700 ;
  font-size: 1.8rem;
  line-height: 2.6rem;
  color: ${COLOR_MAP.LIGHT_GREY};
  margin: 0;
`;

const DateRangePickerWrapper = styled.div`
  position: absolute;
  background-color: ${COLOR_MAP.WHITE};
  box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px, rgba(0, 0, 0, 0.12) 0px 3px 14px 2px;
  display: flex;
  justify-content: center;
  width: 360px;
  border-radius: 4px;
  z-index: 1;
`;

// helper function
const formatDateRange = (dateRange: any) => {
  const from =dateRange.from ? new Date(dateRange.from) : null;
  const to = dateRange.to ? new Date(dateRange.to) : null;;

  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const formattedFrom = from && formatter.format(from);
  const formattedTo =to && formatter.format(to);

  if(formattedFrom && !formattedTo){
    return formattedFrom;
  }
  else if(!formattedFrom && formattedTo){
    return formattedTo
  }

  return `${formattedFrom} to ${formattedTo}`;
}


export const Filters = (props: FilterProps) => {
  const [filters, setFilters] = React.useState<FiltersValueType>(initialFilters);
  const [showDateRange, setShowDateRange] = React.useState<boolean>(false);
  const [customDateRangeValue, setCustomDateRangeValue] = React.useState();
  const [dateFilterSelectedValue, setDateFilterSelectedValue] = React.useState('');
  const { pageNo, handleFitersValueChange, filteredCampaignName } = props;
  const { data: campaignStatusList } = useGetCampaignStatusListQuery(pageNo);

  const handleFiltersValueUpdate = React.useCallback((value: string, type: string) => {
    setShowDateRange(false);

    if(type === 'campaignName' && !value.trim()){
      value = ''
    }

    if (type === 'dateRange') {
      setDateFilterSelectedValue(value);
      if (value === 'Custom Date')
        setShowDateRange(true);
    }
    let updatedFilters = { ...filters };
    updatedFilters[type as keyof FiltersValueType] = value;
    setFilters(updatedFilters);
  }, [filters, setFilters])

  const handleSubmit = React.useCallback(() => {
    let modifiedFilters = {
      campaignSearchName: filters.campaignName,
      campaignStatus: filters.campaignStatus.split(' ').join('_'),
      endDate: '',
      startDate: ''
    };
    const dateRange = getDateRange(filters.dateRange, customDateRangeValue);
    modifiedFilters.startDate = dateRange.startDate;
    modifiedFilters.endDate = dateRange.endDate;

    handleFitersValueChange(modifiedFilters);

  }, [customDateRangeValue, filters.campaignName, filters.campaignStatus, filters.dateRange, handleFitersValueChange])

  const updatedCampaignList = campaignStatusList && campaignStatusList.map((item: string) => {
    let itemValue = item.replaceAll('_',' ');
    return (
      {
        label: itemValue,
        value: itemValue,
      }
    )
  })

  const handleDateRangeApply = React.useCallback((range: any) => {
    if (range) {
      setCustomDateRangeValue(range);
      let customDate = formatDateRange(range);
      setDateFilterSelectedValue(customDate);
    }
    else {
      setDateFilterSelectedValue('');
    }
    setShowDateRange(false);
  }, [])

  const handleDateRangeClickAway = React.useCallback(() => {
    setShowDateRange(false)
    setDateFilterSelectedValue('');
  }, [])

  useEffect(() => {
    if(filteredCampaignName){
      setFilters(prev=>{
        return {
          ...prev,
          campaignName: `${filteredCampaignName}`
        }
      })
    }
  }, [filteredCampaignName])
  

  return (
    <FilterWrapper container direction={'row'} justifyContent={'space-between'}>
      <Grid xs={2}>
        <FilterHeading>Your Campaigns</FilterHeading>
      </Grid>
      <Grid xs={8} gap={'8px'} container alignItems={'center'} justifyContent={'end'}>
        <Grid item xs={3.5}>
          <SingleSelectDropDown
            handleChange={(e) => handleFiltersValueUpdate(e.target.value as string, 'dateRange')}
            id=""
            label="label"
            value='value'
            listItems={DATE_RANGE_VALUES}
            selectedValue={dateFilterSelectedValue}
            placeholder="Date"
            // sx={{height: '36px'}}
            startAdornment={<InputAdornment position="start">{CALENDAR_ICON}</InputAdornment>}
          />
          {showDateRange &&
            <ClickAwayListener onClickAway={handleDateRangeClickAway}>
              <DateRangePickerWrapper>
                <DateRangePicker handleDateRangeApply={handleDateRangeApply} isOpen={showDateRange} />
              </DateRangePickerWrapper>
            </ClickAwayListener>
          }
        </Grid>
        <Grid item xs={2}>
          <SingleSelectDropDown
            handleChange={(e) => handleFiltersValueUpdate(e.target.value as string, 'campaignStatus')}
            id=""
            label="label"
            value='value'
            listItems={updatedCampaignList || []}
            selectedValue={filters?.campaignStatus}
            placeholder="Status"
            // sx={{height: '36px'}}
            startAdornment={<InputAdornment position="start">{FILTER_ICON}</InputAdornment>}
          />
        </Grid>
        <Grid item xs={3.5}>
          <TextBox
            id='campaign-name'
            placeholder="search for Campaign"
            textValue={filters?.campaignName}
            handleChange={(e) => handleFiltersValueUpdate(e.target.value as string, 'campaignName')}
            sx={{
              // '.MuiInputBase-root' : {
              //   height: '36px', 
              // },
            '.MuiInputBase-input' : {
              paddingLeft: '0px'
            }
          }}
          inputProps={{
            startAdornment: <InputAdornment position="start">{SEARCH_ICON}</InputAdornment>,
          }}
          />
        </Grid>
        <Grid item xs={1}>
          <Button  variant='contained' disableRipple onClick={handleSubmit}>Go</Button>
        </Grid>
      </Grid>
    </FilterWrapper>
  )
}

